<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';
import FileUpload from 'vue-upload-component';
import PlatformTooltipIcon from 'dashboard/components-next/PlatformTooltipIcon.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
    FileUpload,
    PlatformTooltipIcon,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edvisibility: { type: String, default: 'global' },
    edattachments: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      visibility: this.edvisibility || 'global',
      show: true,
      attachments: [...(this.edattachments || [])],
      isUploadingAttachment: false,
    };
  },
  validations() {
    // Content is required only if there are no attachments
    const contentValidation = this.attachments.length > 0 ? {} : { required };

    return {
      shortCode: {
        required,
        minLength: minLength(2),
      },
      content: contentValidation,
    };
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    isFormInvalid() {
      // Audio with text is not allowed
      if (this.hasAudioWithTextError) {
        return true;
      }
      // If we have attachments, content is optional
      if (this.attachments.length > 0) {
        return this.v$.shortCode.$invalid;
      }
      return this.v$.shortCode.$invalid || this.v$.content.$invalid;
    },
    // Check if any attachment is an audio file
    hasAudioAttachment() {
      const audioContentTypes = [
        'audio/mpeg',
        'audio/mp3',
        'audio/ogg',
        'audio/opus',
        'audio/wav',
        'audio/x-wav',
        'audio/webm',
        'audio/aac',
        'audio/m4a',
        'audio/x-m4a',
        'audio/flac',
        'audio/amr',
      ];
      return this.attachments.some(att => {
        const contentType = att.content_type || att.file_type || '';
        return (
          audioContentTypes.includes(contentType) ||
          contentType.startsWith('audio/')
        );
      });
    },
    // Check if content has text (ignoring empty HTML tags)
    hasTextContent() {
      if (!this.content) return false;
      // Remove HTML tags and check if there's actual text
      const textOnly = this.content.replace(/<[^>]*>/g, '').trim();
      return textOnly.length > 0;
    },
    // Audio + text is invalid (WhatsApp doesn't support captions on audio)
    hasAudioWithTextError() {
      return this.hasAudioAttachment && this.hasTextContent;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.visibility = 'global';
      this.attachments = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    visibilityCardClass(key) {
      return this.visibility === key
        ? 'bg-n-blue-2 dark:bg-n-blue-1 border-n-blue-3 dark:border-n-blue-4'
        : 'bg-white dark:bg-n-solid-2 border-n-weak dark:border-n-strong';
    },
    async handleFileUpload(fileObject) {
      if (!fileObject || !fileObject.file) return;
      const file = fileObject.file;

      const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
      const MAX_ATTACHMENTS = 3;

      if (file.size > MAX_FILE_SIZE) {
        useAlert(this.$t('CANNED_MGMT.ADD.ATTACHMENTS.ERROR.SIZE'));
        return;
      }

      if (this.attachments.length >= MAX_ATTACHMENTS) {
        useAlert(this.$t('CANNED_MGMT.ADD.ATTACHMENTS.ERROR.LIMIT'));
        return;
      }

      this.isUploadingAttachment = true;
      try {
        const attachment = await this.$store.dispatch(
          'uploadCannedAttachment',
          file
        );
        this.attachments.push(attachment);
      } catch (error) {
        useAlert(this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUploadingAttachment = false;
      }
    },
    removeAttachment(index) {
      this.attachments.splice(index, 1);
    },
    editCannedResponse() {
      // Validate: audio attachments cannot have text content
      if (this.hasAudioWithTextError) {
        useAlert(this.$t('CANNED_MGMT.ADD.ATTACHMENTS.ERROR.AUDIO_WITH_TEXT'));
        return;
      }

      // Show loading on button
      this.editCanned.showLoading = true;

      const payload = {
        id: this.id,
        short_code: this.shortCode,
        content: this.content,
        visibility: this.visibility,
      };

      // Send attachments - always use signed_id
      // For existing attachments (from edattachments): att.signed_id
      // For new uploads: att.signed_id
      payload.attachments = this.attachments
        .filter(att => att && att.signed_id) // Filter out any invalid entries
        .map(att => att.signed_id);

      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', payload)
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }" class="flex items-center gap-1">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <PlatformTooltipIcon identifier="canned_shortcode" />
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }" class="flex items-center gap-1">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
            <PlatformTooltipIcon identifier="canned_message" />
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Visibility Selector -->
        <div class="w-full mt-2">
          <p class="block m-0 text-sm font-medium leading-[1.8] text-n-slate-12">
            {{ $t('CANNED_MGMT.ADD.VISIBILITY.LABEL') }}
          </p>
          <div class="grid grid-cols-2 gap-3">
            <button
              type="button"
              class="p-2 relative rounded-md border border-solid text-left cursor-default"
              :class="visibilityCardClass('global')"
              @click="visibility = 'global'"
            >
              <fluent-icon
                v-if="visibility === 'global'"
                icon="checkmark-circle"
                type="solid"
                class="absolute text-n-brand top-2 right-2"
              />
              <p class="block m-0 text-sm font-medium leading-[1.8] text-n-slate-12">
                {{ $t('CANNED_MGMT.ADD.VISIBILITY.PUBLIC.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ $t('CANNED_MGMT.ADD.VISIBILITY.PUBLIC.DESC') }}
              </p>
            </button>
            <button
              type="button"
              class="p-2 relative rounded-md border border-solid text-left cursor-default"
              :class="visibilityCardClass('personal')"
              @click="visibility = 'personal'"
            >
              <fluent-icon
                v-if="visibility === 'personal'"
                icon="checkmark-circle"
                type="solid"
                class="absolute text-n-brand top-2 right-2"
              />
              <p class="block m-0 text-sm font-medium leading-[1.8] text-n-slate-12">
                {{ $t('CANNED_MGMT.ADD.VISIBILITY.PRIVATE.TITLE') }}
              </p>
              <p class="text-xs text-n-slate-11">
                {{ $t('CANNED_MGMT.ADD.VISIBILITY.PRIVATE.DESC') }}
              </p>
            </button>
          </div>
        </div>

        <!-- Attachment Section -->
        <div class="w-full mt-4">
          <label class="font-medium text-slate-700 dark:text-slate-200 flex items-center gap-1">
            {{ $t('CANNED_MGMT.ADD.ATTACHMENTS.LABEL') }}
            <PlatformTooltipIcon identifier="canned_attachments" />
          </label>

          <!-- Attachment List -->
          <div v-if="attachments.length > 0" class="flex flex-wrap gap-2 mb-3">
            <div
              v-for="(att, index) in attachments"
              :key="att.signed_id || att.id || index"
              class="relative flex items-center p-2 border rounded-md bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 max-w-[200px]"
            >
              <div
                class="mr-2 overflow-hidden text-sm truncate text-slate-700 dark:text-slate-100"
              >
                {{ att.filename }}
              </div>
              <button
                type="button"
                class="text-slate-400 hover:text-red-500"
                @click="removeAttachment(index)"
              >
                <span class="font-bold">×</span>
              </button>
            </div>
          </div>

          <!-- Upload Area -->
          <div
            v-if="attachments.length < 3"
            class="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-lg border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900"
          >
            <FileUpload
              name="attachment"
              class="cursor-pointer"
              :drop="true"
              :multiple="false"
              @input-file="handleFileUpload"
            >
              <div class="flex flex-col items-center text-center cursor-pointer">
                <span v-if="isUploadingAttachment" class="text-slate-500">
                  {{ $t('CANNED_MGMT.ADD.ATTACHMENTS.UPLOADING') }}
                </span>
                <span v-else class="font-medium text-woot-500">
                  <i class="mr-1 ion-android-upload" />
                  {{ $t('CANNED_MGMT.ADD.ATTACHMENTS.UPLOAD_BTN') }}
                </span>
                <span class="mt-1 text-xs text-slate-400">
                  {{ $t('CANNED_MGMT.ADD.ATTACHMENTS.HELPER_TEXT') }}
                </span>
              </div>
            </FileUpload>
          </div>
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="isFormInvalid || editCanned.showLoading"
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];
    // v6.4.20: Auto-grow — editor cresce dinamicamente com o conteúdo
    max-height: none !important;
    overflow-y: visible !important;

    p {
      @apply text-base;
    }
  }
}
</style>
